const PLAN_VALUES = new Set(['solo', 'team', 'business']);
// Links published before the 2026-10 catalogue used "founding".
const PLAN_ALIASES = { founding: 'team', pro: 'team' };
const BILLING_VALUES = new Set(['monthly', 'yearly']);
const ATTRIBUTION_KEYS = [
    'source',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
];
const OUTPUT_KEYS = ['plan', 'billing', ...ATTRIBUTION_KEYS];
// Attribution is a short campaign label, never a URL, email address or
// arbitrary redirect. Keep the browser contract at least as strict as the
// backend so an unsafe value cannot survive in navigation before submission.
const SAFE_ATTRIBUTION_VALUE = /^[A-Za-z0-9][A-Za-z0-9._~+ -]{0,119}$/;

const toSearchParams = (input) => {
    if (input instanceof URLSearchParams) return input;
    if (typeof input === 'string') return new URLSearchParams(input);
    if (!input || typeof input !== 'object') return new URLSearchParams();

    const entries = Object.entries(input).filter(([, value]) => typeof value === 'string');
    return new URLSearchParams(entries);
};

const normalizeAttributionValue = (value) => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return SAFE_ATTRIBUTION_VALUE.test(trimmed) ? trimmed : null;
};

export const normalizePurchaseIntent = (input) => {
    const params = toSearchParams(input);
    const normalized = {};
    const plan = params.get('plan');
    const billing = params.get('billing');

    const planId = PLAN_ALIASES[plan] || plan;
    if (PLAN_VALUES.has(planId)) normalized.plan = planId;
    if (BILLING_VALUES.has(billing)) normalized.billing = billing;

    ATTRIBUTION_KEYS.forEach((key) => {
        const value = normalizeAttributionValue(params.get(key));
        if (value) normalized[key] = value;
    });

    return normalized;
};

export const hasPlanPurchaseIntent = (input) => Boolean(normalizePurchaseIntent(input).plan);

// Catalogue plan id to pre-select on the subscription page.
export const toSubscriptionPlanKey = (plan) => {
    const planId = PLAN_ALIASES[plan] || plan;
    return PLAN_VALUES.has(planId) ? planId : null;
};

export const toRegistrationAttribution = (input) => {
    const normalized = normalizePurchaseIntent(input);
    const { billing, ...attribution } = normalized;

    return {
        ...attribution,
        ...(billing ? { billingCycle: billing } : {}),
    };
};

export const buildPurchaseIntentUrl = (path, input, options = {}) => {
    const url = new URL(path, 'https://playground.finansee.eu');
    const normalized = {
        ...normalizePurchaseIntent(url.search),
        ...normalizePurchaseIntent(input),
    };
    const params = new URLSearchParams();

    OUTPUT_KEYS.forEach((key) => {
        if (normalized[key]) params.set(key, normalized[key]);
    });

    if (typeof options.email === 'string') {
        const email = options.email.trim();
        if (email && email.length <= 254 && !/\s/.test(email)) {
            params.set('email', email);
        }
    }

    const search = params.toString();
    return `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
};

export const withSelectedPlan = (input, plan) => normalizePurchaseIntent({
    ...normalizePurchaseIntent(input),
    plan,
});
